'use client';

import React, { useEffect, useRef } from 'react';
import { type ContentfulComponentProps } from '../type';
import { useCssVar, useElementSize } from '@/hooks';
import { useStickyCtaScrollBehavior } from '@/src/hooks/useStickyCtaScrollBehavior';
import { StickyCtaUpdateOffsetsRef, useStickyCtaOffsets } from '@/src/hooks/useStickyCtaOffsets';
interface StickyCtaTriggerProps extends ContentfulComponentProps<SiteStickyCta> {
    isTopLevelSticky?: boolean;
}

export function StickyCtaTrigger({ asset, isTopLevelSticky = false }: StickyCtaTriggerProps): React.JSX.Element {
    const scrollUpBehavior = asset.fields.presentation.styles['Scroll-up Behavior'];

    const triggerRef = useRef<HTMLDivElement>(null);

    const updateOffsetsRef = useRef<() => void>(() => {});

    const [observeNavSize, navSize] = useElementSize();

    const [observeStickyBarSize, stickyBarSize] = useElementSize();

    // Keep this effect in the component so the existing effect order remains:
    // size observation -> scroll/session behavior -> offset calculation.
    useEffect(() => {
        observeNavSize(document.querySelector('[data-componenttype="Global Nav"]'));

        observeStickyBarSize(document.querySelector('[class*="stickybar"]'));

        return () => {
            observeNavSize(null);
            observeStickyBarSize(null);
        };
    }, [observeNavSize, observeStickyBarSize]);

    useStickyCtaScrollBehavior({
        scrollUpBehavior,
        isTopLevelSticky,
        triggerRef,
        updateOffsetsRef: updateOffsetsRef as StickyCtaUpdateOffsetsRef,
    });

    const offsets = useStickyCtaOffsets({
        scrollUpBehavior,
        isTopLevelSticky,
        triggerRef,
        navSize,
        stickyBarSize,
        updateOffsetsRef: updateOffsetsRef as StickyCtaUpdateOffsetsRef,
    });

    useCssVar('--sticky-cta-top', offsets['--sticky-cta-top']);

    useCssVar('--sticky-bar-top', offsets['--sticky-bar-top']);

    useCssVar('--sticky-bar-reserve', offsets['--sticky-bar-reserve']);

    useCssVar('--nav-reserve', offsets['--nav-reserve']);

    useCssVar('--channel-guide-top', offsets['--channel-guide-top']);

    return <div ref={triggerRef} data-note="sticky-trigger" />;
}
